import { z } from "zod";
import { PROPOSAL_STATUSES } from "./common";

/** SLA = Scheduling Agreement / Schedule Line release (NOT "Service-Level Agreement"),
 * per GSP SLA Confirmation Process Technical Design. Mirrors po.ts field-for-field
 * with SLA-specific naming (ReleaseNumber/SLNumber/SLANumber/SLALineItem). */
export const slaFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  slaNumber: z.string().optional(),
  /** true = Firm SLA Change (query already-confirmed lines, ConfirmationStatus="C") —
   * this already IS the documented §4.1 ConfirmationStatus field for this module
   * (U/C mapped to false/true), so it is not duplicated as a separate parameter. */
  alreadyConfirmed: z.coerce.boolean().optional().default(false),
  /** ERPID — filters to lines whose vendor is assigned to this ERP system
   * (erp_systems.name, e.g. "SAP_C11"), per §4.1 Input Fields. */
  erpId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type SlaFilterInput = z.infer<typeof slaFilterSchema>;

export const slaSplitLineSchema = z.object({
  slaLineId: z.number().int(),
  proposedQty: z.number().positive(),
  proposedDeliveryDate: z.string(),
  reasonId: z.number().int(),
  comment: z.string().optional(),
});
export const slaSplitSubmitSchema = z.object({ lines: z.array(slaSplitLineSchema).min(1) });
export type SlaSplitSubmitInput = z.infer<typeof slaSplitSubmitSchema>;

export const slaConfirmSelectSchema = z.object({ slaLineIds: z.array(z.number().int()).min(1) });

export const slaProposalDecisionSchema = z.object({
  proposalIds: z.array(z.number().int()).min(1),
  decision: z.enum(["approve", "reject"]),
  comment: z.string().optional(),
});

export const slaCommentSchema = z.object({
  slaLineId: z.number().int(),
  comment: z.string().min(1),
});

/** Supplier SLA Proposal Input — §4.2. */
export const slaProposalsFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  slaNumber: z.string().optional(),
  erpId: z.string().optional(),
});
export type SlaProposalsFilterInput = z.infer<typeof slaProposalsFilterSchema>;

/** Supplier SLA Proposal History Report Input — §4.4. */
export const slaProposalHistoryFilterSchema = slaProposalsFilterSchema.extend({
  proposalStatus: z.enum(["all", ...PROPOSAL_STATUSES]).optional().default("all"),
});
export type SlaProposalHistoryFilterInput = z.infer<typeof slaProposalHistoryFilterSchema>;

/** Confirmed SLA Report Input — §4.3. */
export const slaReportFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  erpId: z.string().optional(),
});
export type SlaReportFilterInput = z.infer<typeof slaReportFilterSchema>;

/** SLA Confirm Audit Log Input — §4.5. */
export const slaAuditLogFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});
export type SlaAuditLogFilterInput = z.infer<typeof slaAuditLogFilterSchema>;
